import React from "react";
import { Loading } from "components/panels";
import { CommonSignatoryFieldProps, ContactField, DebugField, EmailField, InvitationTitleField, LanguageField, RepresentingField, TemplateBodyField, TemplateSubjectField } from "./SignatoryFields";
import { Section } from "components/MainSectionTemplate/types";
import ESignParticipantProgressBadge from "eSign/components/EsignParticipantProgressBadge";
import { PhosphorXButton } from "components/Atoms/PhosphorIconButton";
import { getInvitationTranslation } from "components/MessageTemplate/hooks";
import { getLcidFromParticipant } from "utilities/esigningUtils";
import { useContact } from "utilities/hookutils";

interface Props {
  esignDocument: server.dto.ESignDocument
  signer: server.dto.ESignParticipant;
  progress: server.dto.ESignParticipantProgress
  draft?: {
    onSignerChange: (signer: server.dto.ESignParticipant) => void;
    onRemoveSigner: () => void;
    isRemovingSigner: boolean;
    noSignerError: boolean;
    onCompanyChange?: () => void;
  }
}

export const SignerPanel = (props: Props): Section => {
  const hasSigner = props.signer != null;
  const editDisabled = props.esignDocument.lifeCycle.value !== "draft";
  const displayInvitationMessage = props.esignDocument.lifeCycle.value === "draft" || props.esignDocument.lifeCycle.value === "review";

  const canDeleteParticipant = !editDisabled && hasSigner && !props.draft?.isRemovingSigner;

  const contact = useContact(props.signer?.contact);

  const fieldProps: CommonSignatoryFieldProps = {
    esignDocument: props.esignDocument,
    participant: props.signer,
    role: "signer",
    hasParticipant: hasSigner,
    editDisabled,
    progress: props.progress,
    contact
  }

  const onTemplateSelected = async (template: server.dto.MessageTemplate, language: server.dto.Languages) => {
    const translation = await getInvitationTranslation(template.messageTemplateId, language);

    const updatedSigner = await window.CoreApi.ESign.SetParticipantFromContact({
      companyId: props.signer.company?.companyId,
      contactId: contact.contactId,
      contactCulture: getLcidFromParticipant(props.signer.linkedToUser, contact),
      documentId: props.esignDocument.documentId,
      emailAddress: props.signer.email,
      role: "signer",
      invitationSubject: translation.subject,
      invitationBody: translation.body
    });

    props.draft.onSignerChange(updatedSigner.data.data);
  }

  return {
    key: "signer",
    title: <div className="flex justify-space-between">
      {RESX.ESign.Signer}
      {props.signer != null && <div>
        {canDeleteParticipant && <PhosphorXButton
          layout="in-square"
          iconColor="error"
          onClick={props.draft.onRemoveSigner}
        />}
        {props.draft?.isRemovingSigner && <Loading size="tiny" />}
        <ESignParticipantProgressBadge signedUtc={props.signer.signature?.signedUtc} progress={props.progress} />
      </div>}
    </div>,
    type: "regular",
    fields: hasSigner ? [
      ContactField({ ...fieldProps, hasError: props.draft?.noSignerError, senderSigner: "signer" }),
      RepresentingField({ ...fieldProps, onCompanyChange: props.draft?.onCompanyChange }),
      EmailField(fieldProps),
      LanguageField(fieldProps),
      DebugField(fieldProps),
      InvitationTitleField({ ...fieldProps, display: displayInvitationMessage, onTemplateSelected }),
      TemplateSubjectField({ ...fieldProps, display: displayInvitationMessage }),
      TemplateBodyField({ ...fieldProps, display: displayInvitationMessage }),
    ] : [
      ContactField({ ...fieldProps, hasError: props.draft?.noSignerError, senderSigner: "signer" }),
    ],
  }
}


